// fileTagsUtils.js - Vyčištěná verze bez debug logů

// src/utils/fileTagsUtils.js
import i18next from 'i18next';
import { fileTags as fileTagsApi } from '../services/api';

// ✅ MINIMÁLNÍ FALLBACK - pouze "all" tag pokud selže vše ostatní
const MINIMAL_FALLBACK = [
  { 
    code: 'all', 
    labels: { 
      en: 'All Files', 
      cs: 'Všechny soubory' 
    },
    enabled: true 
  }
];

// Helper to normalize tags (convert old format to new if needed)
export const normalizeTags = (tags) => {
  if (!tags || !Array.isArray(tags)) {
    return [];
  }
  
  return tags.map(tag => {
    if (typeof tag.label === 'string') {
      // Convert old format to new multilingual format
      return {
        ...tag,
        labels: { 
          en: tag.label,
          cs: tag.label // Default to same value for Czech
        }
      };
    }
    return tag;
  });
};

// Get enabled file tags from company settings
export const getEnabledFileTags = (settings) => {
  // Pokud nemáme settings, vrať prázdné pole
  if (!settings?.fileTags || settings.fileTags.length === 0) {
    console.warn('⚠️ No file tags in settings, returning empty array');
    return [];
  }

  // Normalize the tags to ensure they have the multilingual structure
  const normalizedTags = normalizeTags(settings.fileTags);
  
  // Check if "all" tag exists
  const hasAllTag = normalizedTags.some(tag => tag.code === 'all');
  
  let allTags = [];
  if (!hasAllTag) {
    // Add the "all" tag if it doesn't exist
    allTags = [MINIMAL_FALLBACK[0], ...normalizedTags];
  } else {
    allTags = [...normalizedTags];
  }
  
  // Return only enabled tags
  const enabledTags = allTags.filter(tag => tag.enabled !== false);
  
  return enabledTags;
};

// ✅ Get the localized label for a tag
export const getLocalizedLabel = (tag, currentLanguage = null) => {
  if (!tag) {
    console.warn('⚠️ getLocalizedLabel called with null/undefined tag');
    return '';
  }
  
  // Use provided language or get current language from i18next
  const language = currentLanguage || i18next.language || 'en';
  
  // Check for multilingual labels
  if (tag.labels && typeof tag.labels === 'object' && tag.labels[language]) {
    return tag.labels[language];
  }
  
  // Fallback to English
  if (tag.labels && tag.labels.en) {
    return tag.labels.en;
  }
  
  // Fallback to old format or just the code
  return tag.label || tag.code || '';
};

// Format tag for dropdown display with explicit language support
export const formatTagForDisplay = (tag, currentLanguage = null) => {
  return {
    value: tag.code,
    label: getLocalizedLabel(tag, currentLanguage)
  };
};

// Get tag options for dropdowns with language parameter
export const getTagOptions = (settings, currentLanguage = null) => {
  const tags = getEnabledFileTags(settings);
  return tags.map(tag => formatTagForDisplay(tag, currentLanguage));
};

// Get a single tag by code
export const getTagByCode = (settings, code) => {
  const tags = getEnabledFileTags(settings);
  
  // Try to find the tag by code
  const tag = tags.find(tag => tag.code === code);
  
  if (tag) return tag;
  
  // Fallback only if looking for "all" tag
  if (code === 'all') {
    return MINIMAL_FALLBACK[0];
  }
  
  // Last resort, return null - caller should handle this
  console.warn(`⚠️ Tag with code "${code}" not found`);
  return null;
};

// Add a function to fetch file tags directly
export const fetchFileTags = async () => {
  try {
    const { data } = await fileTagsApi.getAll();
    return data && data.fileTags ? getEnabledFileTags(data) : MINIMAL_FALLBACK;
  } catch (error) {
    console.error('Error fetching file tags:', error);
    // Return minimal fallback as last resort
    return [...MINIMAL_FALLBACK];
  }
};

// Validate a tag against the available tags
export const isValidTag = (settings, tagCode) => {
  const enabledTags = getEnabledFileTags(settings);
  
  // Check if the tag exists in the enabled tags
  return enabledTags.some(tag => tag.code === tagCode);
};

// Get a safe tag (validated against settings, or default to first available)
export const getSafeTag = (settings, tagCode) => {
  if (isValidTag(settings, tagCode)) {
    return tagCode;
  }
  
  // Return first available tag or 'all' as fallback
  const enabledTags = getEnabledFileTags(settings);
  if (enabledTags.length > 0) {
    return enabledTags[0].code;
  }
  
  return 'all';
};